/**
 * ImportConfirmationModal.tsx
 * Modal for confirming instrument imports with instant execution
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Chip, Alert, Listbox, ListboxItem, Spinner, useToastNotification } from '@enfomentis/ui';
import { Plus } from 'lucide-react';
import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';
import { instrumentService } from '@/services/InstrumentService';


interface ImportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInstruments: InstrumentCatalogEntry[];
  /** Called after import with IDs of newly created instruments. Parent owns close/navigation. */
  onImportComplete: (importedIds: string[]) => void;
}

export const ImportConfirmationModal: React.FC<ImportConfirmationModalProps> = ({
  isOpen,
  onClose,
  selectedInstruments,
  onImportComplete,
}): React.ReactElement | null => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importPreview, setImportPreview] = useState<{
    newInstruments: InstrumentCatalogEntry[];
    duplicates: Array<{ catalogEntry: InstrumentCatalogEntry; existingInstruments: { displayName: string }[] }>;
    isLoading: boolean;
  }>({
    newInstruments: [],
    duplicates: [],
    isLoading: true
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const { addToast } = useToastNotification();

  // Create combined items array for Listbox (following BrokerSelector pattern)
  const instrumentListItems = useMemo(() => {
    const items: Array<{
      key: string;
      type: 'new' | 'duplicate';
      catalogEntry: InstrumentCatalogEntry;
      existingInstruments: { displayName: string }[];
    }> = [];

    // Add new instruments
    importPreview.newInstruments.forEach(instrument => {
      items.push({
        key: instrument.id,
        type: 'new',
        catalogEntry: instrument,
        existingInstruments: []
      });
    });

    // Add duplicate instruments
    importPreview.duplicates.forEach(item => {
      items.push({
        key: item.catalogEntry.id,
        type: 'duplicate',
        catalogEntry: item.catalogEntry,
        existingInstruments: item.existingInstruments
      });
    });

    return items;
  }, [importPreview.newInstruments, importPreview.duplicates]);

  // Scan for duplicates when modal opens
  useEffect(() => {
    if (!isOpen || selectedInstruments.length === 0) return;

    const scanForDuplicates = async (): Promise<void> => {
      try {
        setImportPreview(prev => ({ ...prev, isLoading: true }));
        
        // Check for duplicates without actually importing
        const newInstruments: InstrumentCatalogEntry[] = [];
        const duplicates: Array<{ catalogEntry: InstrumentCatalogEntry; existingInstruments: { displayName: string }[] }> = [];
        
        // Check each instrument individually for existing copies
        for (const instrument of selectedInstruments) {
          const existingInstruments = await instrumentService.getByReferenceId(instrument.id);
          
          if (existingInstruments.length > 0) {
            // This is a duplicate - store all existing instances
            duplicates.push({
              catalogEntry: instrument,
              existingInstruments: existingInstruments
            });
          } else {
            // This is new
            newInstruments.push(instrument);
          }
        }

        setImportPreview({
          newInstruments,
          duplicates,
          isLoading: false
        });

        // Pre-select new instruments (user will want these by default)
        const newKeys = new Set(newInstruments.map(inst => inst.id));
        setSelectedKeys(newKeys);

      } catch (error) {
        console.error('Failed to scan for duplicates:', error);
        setImportPreview({
          newInstruments: selectedInstruments, // Fallback: treat all as new
          duplicates: [],
          isLoading: false
        });
        setSelectedKeys(new Set(selectedInstruments.map(inst => inst.id)));
      }
    };

    scanForDuplicates();
  }, [isOpen, selectedInstruments]);

  const handleStartImport = useCallback(async (): Promise<void> => {
    try {
      // Extract catalog IDs from user's selection in Listbox
      const catalogIds = Array.from(selectedKeys);

      if (catalogIds.length === 0) {
        addToast({
          title: 'No Selection',
          description: 'Please select at least one instrument to import',
          color: 'warning',
          timeout: 3000
        });
        return;
      }
      
      // Determine if we're trying to create duplicates
      const duplicateIds = importPreview.duplicates.map(dup => dup.catalogEntry.id);
      const userWantsDuplicates = catalogIds.some(id => duplicateIds.includes(id));
      
      // Use InstrumentService to import selected instruments
      const importResult = await instrumentService.importFromCatalog(catalogIds, undefined, userWantsDuplicates);
      
      // Calculate results
      const importedCount = importResult.created.length;
      const duplicateCount = importResult.duplicates.length;
      const errorCount = importResult.errors.length;

      // Pass created IDs back to caller for auto-selection
      const importedIds = importResult.created.map(inst => inst.id);
      onImportComplete(importedIds);

      // Show result toast notification based on actual user intent
      const actuallySkipped = userWantsDuplicates ? 0 : duplicateCount;
      
      if (importedCount > 0) {
        let description = `${importedCount} instrument${importedCount === 1 ? '' : 's'} imported successfully`;
        
        if (userWantsDuplicates && duplicateCount > 0) {
          // User explicitly created duplicates
          const duplicatesCreated = Math.min(importedCount, duplicateCount);
          if (duplicatesCreated > 0) {
            description += ` (${duplicatesCreated} additional cop${duplicatesCreated === 1 ? 'y' : 'ies'} from existing templates)`;
          }
        } else if (actuallySkipped > 0) {
          // Some were actually skipped
          description += `, ${actuallySkipped} duplicate${actuallySkipped === 1 ? '' : 's'} skipped`;
        }
        
        if (errorCount > 0) {
          description += `, ${errorCount} error${errorCount === 1 ? '' : 's'} occurred`;
        }
        
        addToast({
          title: 'Import Completed',
          description,
          color: errorCount > 0 ? 'warning' : 'success',
          timeout: 5000
        });
      } else if (actuallySkipped > 0) {
        addToast({
          title: 'Import Skipped',
          description: `${actuallySkipped} instrument${actuallySkipped === 1 ? '' : 's'} already exist in your library`,
          color: 'primary',
          timeout: 5000
        });
      } else {
        addToast({
          title: 'Import Failed',
          description: `${errorCount} error${errorCount === 1 ? '' : 's'} occurred during import`,
          color: 'danger',
          timeout: 5000
        });
      }

      // Parent's onImportComplete callback owns closing and navigation

    } catch (error) {
      console.error('Import failed:', error);
      
      const errorMsg = error instanceof Error ? error.message : 'Import failed';
      setErrorMessage(errorMsg);
      setHasError(true);

      // Show error toast notification
      addToast({
        title: 'Import Failed',
        description: 'An error occurred during import. Please try again.',
        color: 'danger',
        timeout: 5000
      });
    }
  }, [selectedKeys, importPreview.duplicates, onImportComplete, addToast]);

  const handleRetry = useCallback((): void => {
    setHasError(false);
    setErrorMessage('');
    handleStartImport();
  }, [handleStartImport]);


  if (!isOpen) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose} 
      size="2xl"
      placement="center"
      scrollBehavior="inside"
      shouldBlockScroll={false}
      disableAnimation
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Plus size={20} className="text-primary" />
            <h2 className="text-lg font-semibold">Import Instruments</h2>
          </div>
          <p className="text-sm text-foreground-500 font-normal">
            Import {selectedInstruments.length} selected instruments to your collection
          </p>
        </ModalHeader>

        <ModalBody>
          {!hasError && (
            <div className="space-y-4">
              {importPreview.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <Spinner size="lg" />
                    <p className="text-sm text-foreground-600">Checking for duplicates...</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Import Selection</h3>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        <Chip size="sm" color="success" variant="flat">
                          {importPreview.newInstruments.length} New
                        </Chip>
                        {importPreview.duplicates.length > 0 && (
                          <Chip size="sm" color="warning" variant="flat">
                            {importPreview.duplicates.length} Duplicates
                          </Chip>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="light"
                        onPress={() => {
                          const allKeys = instrumentListItems.map(item => item.key);
                          const isAllSelected = allKeys.every(key => selectedKeys.has(key));
                          
                          if (isAllSelected) {
                            // Deselect all
                            setSelectedKeys(new Set());
                          } else {
                            // Select all
                            setSelectedKeys(new Set(allKeys));
                          }
                        }}
                      >
                        {instrumentListItems.every(item => selectedKeys.has(item.key)) ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                  </div>

                  <div className="border border-divider rounded-lg">
                    <Listbox
                      aria-label="Select instruments to import"
                      selectionMode="multiple"
                      selectedKeys={selectedKeys}
                      onSelectionChange={(keys: 'all' | Set<React.Key>) => {
                        if (keys !== 'all') {
                          setSelectedKeys(keys as Set<string>);
                        }
                      }}
                      className="max-h-96 overflow-y-auto [&_[data-selected=true]]:!bg-transparent [&_[aria-selected=true]]:!bg-transparent [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                    >
                      {instrumentListItems.map((item) => {
                        // Build status indicator and description
                        const statusIndicator = (
                          <div className={`w-2 h-2 rounded-full ${item.type === 'new' ? 'bg-success' : 'bg-warning'}`}></div>
                        );

                        const statusDescription = item.type === 'new' ? (
                          <span className="text-xs text-success-600">New instrument</span>
                        ) : (
                          <span className="text-xs text-warning-600">
                            {item.existingInstruments.length > 1 
                              ? `${item.existingInstruments.length} existing copies` 
                              : 'Already exists'}: "{item.existingInstruments.map(inst => inst.displayName).join(', ')}"
                          </span>
                        );

                        return (
                          <ListboxItem
                            key={item.key}
                            textValue={`${item.catalogEntry.symbol} - ${item.catalogEntry.name}`}
                            startContent={statusIndicator}
                            description={statusDescription}
                            shouldHighlightOnFocus={false}
                            className="data-[hover=true]:bg-default-100"
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{item.catalogEntry.symbol} - {item.catalogEntry.name}</span>
                            </div>
                          </ListboxItem>
                        );
                      })}
                    </Listbox>
                  </div>

                  {importPreview.duplicates.length > 0 && (
                    <Alert hideIconWrapper color="warning" variant="bordered">
                      Items that already exist in your instrument library are not selected for import by default.
                      If you want to create duplicates/copies in your library, manually select them from list above.
                    </Alert>
                  )}
                </>
              )}
            </div>
          )}

          {hasError && (
            <div className="space-y-4">
              <Alert color="danger" variant="bordered" title="Import Failed">
                An error occurred during the import process. Please try again or contact support if the problem persists.
              </Alert>
              
              {errorMessage && (
                <Alert color="warning" variant="bordered" title="Error Details">
                  {errorMessage}
                </Alert>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          {!hasError && (
            <>
              <Button variant="flat" onPress={onClose}>
                Cancel
              </Button>
              <Button 
                color="primary" 
                onPress={handleStartImport}
                startContent={<Plus size={16} />}
                isDisabled={importPreview.isLoading || selectedKeys.size === 0}
              >
                {importPreview.isLoading 
                  ? 'Scanning...' 
                  : selectedKeys.size === 0 
                    ? 'Select Instruments'
                    : `Import ${selectedKeys.size} Instrument${selectedKeys.size === 1 ? '' : 's'}`
                }
              </Button>
            </>
          )}

          {hasError && (
            <>
              <Button variant="flat" onPress={handleRetry}>
                Try Again
              </Button>
              <Button 
                color="danger" 
                variant="bordered"
                onPress={onClose}
              >
                Close
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};